'use client';

import type { FC } from 'react';
import { useEffect, useRef } from 'react';
import {
    VIDEO_ERROR_BY_CONTEXT,
    EXCLUSIVE_LAUNCH_ZONE_DEFAULTS,
    SESSION_KEYS,
    EXCLUSIVE_LAUNCH_TYPE
} from '@/utils/constants';
import { GameUrl } from '@/app/components/game/subcomponents/game-url/game-url';
import { Video } from '@/app/elements/video/video';
import { remove, retrieve, store } from '@/utils/storage';
import { InfoButton } from '@/app/components/game/subcomponents/info-button';
import { scrollToStoredElement } from '@/utils/scroll/scroll';
import type { LayoutSlice } from '@/types/zones/zone';
import type { TrailerProps } from '@/types/zones/exclusive-zone';
import { classes } from '@/utils/classes/classes';
import BadgeFactoryClient from '@/app/components/badge/badge-factory.client';
import { TextBadge } from '@/app/components/badge/layouts/text/text-badge';

import './styles/@theme';
import { getText } from '@/utils/text/text';

type ExclusiveTrailerProps = {
    layoutContent: LayoutSlice;
    trailerProps: TrailerProps;
    isLandscape: boolean;
    exclusiveType?: string;
    isTrailerReady?: boolean;
};

export const ExclusivePostLaunchTrailer: FC<ExclusiveTrailerProps> = ({
    layoutContent,
    trailerProps,
    isLandscape,
    exclusiveType,
    isTrailerReady = true
}) => {
    const content = { ...layoutContent.primary, ...trailerProps };
    //TODO implement this after throttle is done
    // const promoThrottle = useSelector((state: RootState) =>
    //     state?.pageConfig?.product?.data?.features?.some(
    //         (item:any) => item?.feature_name === PRODUCT_THROTTLES.HAS_PROMO_BUTTON
    //     )
    // );

    const isPromoAvailable = content.promo_cta_button_title && content.promo_url; /*&& promoThrottle*/
    const playNowButtonText = content.cta_button_text || EXCLUSIVE_LAUNCH_ZONE_DEFAULTS.PLAY_NOW_BUTTON;
    const componentRef = useRef<HTMLDivElement>(null);
    const orientationClass = isLandscape ? 'landscape' : 'portrait';
    const isEarlyAccess = exclusiveType === EXCLUSIVE_LAUNCH_TYPE.EARLY_ACCESS;

    useEffect(() => {
        const elementId = retrieve(SESSION_KEYS.SCROLL_TO) as string;
        scrollToStoredElement({ id: elementId, offset: 200 });
        remove(SESSION_KEYS.SCROLL_TO);

        const componentElement = componentRef?.current;
        const handleMousedown = () => {
            //TODO check after prelaunch is done
            // exclusiveModalSeen(zoneUid + ui_version);
        };

        if (!componentElement) {
            return;
        }
        componentElement.addEventListener('mousedown', handleMousedown);

        return () => {
            componentElement.removeEventListener('mousedown', handleMousedown);
        };
    }, []);

    if (!content.url) {
        return null;
    }

    const handlePromoClick = () => {
        store({ key: SESSION_KEYS.SCROLL_TO, value: 'exclusive-post-launch' }, 'session');
    };

    return (
        <div ref={componentRef} className={classes('exclusive-post-launch-trailer', orientationClass)}>
            {!isLandscape && isEarlyAccess && (
                <BadgeFactoryClient>
                    <TextBadge label={getText({ text: 'components.badge.earlyAccess' })} />
                </BadgeFactoryClient>
            )}
            <GameUrl launcherUrlProps={content.launchParameters}>
                {isTrailerReady ? (
                    <Video
                        uid={content.gameContent.uid}
                        src={content.url}
                        videoId={content.id}
                        amplitudeEvents={content.amplitudeEvents as Record<string, unknown> | undefined}
                        launchParameters={content.launchParameters}
                        poster={content.poster}
                        contextError={VIDEO_ERROR_BY_CONTEXT.POST_LAUNCH}
                        hasVideoDetails={false}
                        videosPlaying={true}
                    />
                ) : (
                    <div className="video-wrapper" aria-hidden="true" data-testid="exclusive-trailer-placeholder">
                        <div className="video">
                            {content.poster && <img src={content.poster} alt="" loading="eager" decoding="async" />}
                        </div>
                    </div>
                )}
            </GameUrl>
            {!isLandscape && (
                <div>
                    <div className="game-info-wrapper">
                        <div className="game-info" data-test-id="exclusive-info-button">
                            <InfoButton uid={content.gameContent.uid} />
                        </div>
                        <div className="cta-buttons-wrapper" data-test-id="exclusive-post-launch-buttons">
                            <GameUrl launcherUrlProps={content.launchParameters}>
                                <div className="play-now-button">{playNowButtonText}</div>
                            </GameUrl>
                            {!!isPromoAvailable && (
                                <button id="promo-button" className="promo-available-button" onClick={handlePromoClick}>
                                    {content.promo_cta_button_title}
                                </button>
                            )}
                        </div>
                    </div>
                    <p className="legal" data-test-id="exclusive-copyright">
                        {content.legalText}
                    </p>
                </div>
            )}
        </div>
    );
};
